import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import {
  TrendingUp, TrendingDown, Users, Package, Clock, Download,
  ArrowUpRight, ArrowDownRight, DollarSign, ShoppingBag, BarChart3,
  RefreshCw, ChevronDown
} from 'lucide-react';

interface AdvancedAnalyticsProps {
  storeApiKey: string;
  isReadOnly?: boolean;
}

type Period = '7d' | '30d' | '90d' | '1y';

const PERIODS: { id: Period; label: string }[] = [
  { id: '7d', label: '7 Days' },
  { id: '30d', label: '30 Days' },
  { id: '90d', label: '90 Days' },
  { id: '1y', label: '1 Year' },
];

const AdvancedAnalytics: React.FC<AdvancedAnalyticsProps> = ({ storeApiKey, isReadOnly = false }) => {
  const [period, setPeriod] = useState<Period>('30d');
  const [loading, setLoading] = useState(true);
  const [comparison, setComparison] = useState<any>(null);
  const [customers, setCustomers] = useState<any>(null);
  const [products, setProducts] = useState<any>(null);
  const [cashiers, setCashiers] = useState<any>(null);
  const [trends, setTrends] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'comparison' | 'customers' | 'products' | 'cashiers' | 'trends'>('comparison');
  const [productSort, setProductSort] = useState<'revenue' | 'qty'>('revenue');

  const headers = () => {
    const token = localStorage.getItem('kangavena:cashierToken') || '';
    return { 'x-store-api-key': storeApiKey, ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const h = headers();
      const [comp, cust, prod, cash, trend] = await Promise.all([
        fetch(`/api/analytics/period-comparison?apiKey=${storeApiKey}&period=${period}`, { headers: h }).then(r => r.ok ? r.json() : null),
        fetch(`/api/analytics/customers?apiKey=${storeApiKey}&limit=20`, { headers: h }).then(r => r.ok ? r.json() : null),
        fetch(`/api/analytics/products?apiKey=${storeApiKey}&sort=${productSort}&limit=20`, { headers: h }).then(r => r.ok ? r.json() : null),
        fetch(`/api/analytics/cashiers?apiKey=${storeApiKey}&period=${period}`, { headers: h }).then(r => r.ok ? r.json() : null),
        fetch(`/api/analytics/sales-trends?apiKey=${storeApiKey}`, { headers: h }).then(r => r.ok ? r.json() : null),
      ]);
      setComparison(comp);
      setCustomers(cust);
      setProducts(prod);
      setCashiers(cash);
      setTrends(trend);
      // Show error if ALL fetches failed (likely auth issue)
      if (!comp && !cust && !prod && !cash) {
        console.error('All analytics fetches failed — check auth');
      }
    } catch (err) { console.error('Analytics fetch error:', err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, [storeApiKey, period, productSort]);

  const exportCSV = (type: 'transactions' | 'customers' | 'products') => {
    const url = `/api/analytics/export?apiKey=${storeApiKey}&type=${type}&period=${period}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = `kangavena-${type}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const StatCard = ({ label, value, sub, trend, trendUp, icon: Icon, color = 'cyan' }: any) => {
    const colorMap: Record<string, string> = {
      cyan: 'from-cyan-500 to-cyan-600',
      emerald: 'from-emerald-500 to-emerald-600',
      violet: 'from-violet-500 to-violet-600',
      amber: 'from-amber-500 to-amber-600',
      rose: 'from-rose-500 to-rose-600',
      slate: 'from-slate-500 to-slate-600',
    };
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[9px] font-bold uppercase tracking-widest text-slate-500">{label}</span>
          <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${colorMap[color] || colorMap.cyan} flex items-center justify-center text-white`}>
            <Icon size={13} />
          </div>
        </div>
        <p className="text-[18px] font-black text-slate-800 leading-none">{value}</p>
        {sub && <p className="text-[9px] text-slate-500 mt-1">{sub}</p>}
        {trend && (
          <div className={`flex items-center gap-1 text-[9px] font-bold mt-1.5 ${trendUp ? 'text-emerald-600' : 'text-rose-600'}`}>
            {trendUp ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}
            {trend}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-slate-50/50">
      {/* Header */}
      <div className="shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3 px-3 sm:px-6 py-3 sm:py-4 border-b border-slate-200 bg-white/80 backdrop-blur-md">
        <div>
          <h2 className="text-[15px] font-black text-slate-800">Advanced Analytics</h2>
          <p className="text-[10px] text-slate-500">Deep insights: comparisons, customers, products, staff</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-slate-100 rounded-xl p-0.5 sm:p-1 overflow-x-auto no-scrollbar">
            {PERIODS.map(p => (
              <button key={p.id} onClick={() => setPeriod(p.id)}
                className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-[9px] sm:text-[10px] font-bold whitespace-nowrap transition-all ${period === p.id ? 'bg-white text-brand-primary shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                {p.label}
              </button>
            ))}
          </div>
          <button onClick={fetchAll} disabled={loading}
            className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors disabled:opacity-50">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="shrink-0 flex items-center gap-0.5 sm:gap-1 px-2 sm:px-6 pt-2 sm:pt-3 border-b border-slate-200 bg-white/60 overflow-x-auto no-scrollbar">
        {([
          { id: 'comparison', label: 'Period Comparison', icon: TrendingUp },
          { id: 'trends', label: 'Sales Trends', icon: BarChart3 },
          { id: 'customers', label: 'Customers', icon: Users },
          { id: 'products', label: 'Products', icon: Package },
          { id: 'cashiers', label: 'Staff Performance', icon: Clock },
        ] as const).map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-2 rounded-t-lg text-[9px] sm:text-[11px] font-semibold whitespace-nowrap transition-all ${activeTab === t.id ? 'bg-brand-primary text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}>
              <Icon size={12} /> <span className="hidden sm:inline">{t.label}</span><span className="sm:hidden">{t.label.split(' ')[0]}</span>
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw size={20} className="animate-spin text-brand-primary mr-2" />
            <span className="text-[11px] text-slate-500">Loading analytics...</span>
          </div>
        ) : !comparison && !customers && !products && !cashiers ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-400">
            <BarChart3 size={28} className="mb-2 opacity-40" />
            <p className="text-[12px] font-semibold">No analytics data available</p>
            <p className="text-[10px] mt-1">Make sure you're signed in as a manager or have a valid API key</p>
            <button onClick={fetchAll} className="mt-3 px-4 py-2 rounded-lg bg-brand-primary text-white text-[10px] font-bold">Retry</button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* PERIOD COMPARISON */}
            {activeTab === 'comparison' && comparison && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <StatCard label={`This Period Revenue`} value={`Rs. ${comparison.thisPeriod.revenue.toLocaleString()}`} sub={`${comparison.thisPeriod.startDate} → ${comparison.thisPeriod.endDate}`} trend={comparison.changes.revenuePct} trendUp={comparison.changes.revenueUp} icon={DollarSign} color="emerald" />
                  <StatCard label={`Last Period Revenue`} value={`Rs. ${comparison.lastPeriod.revenue.toLocaleString()}`} sub={`${comparison.lastPeriod.startDate} → ${comparison.lastPeriod.endDate}`} icon={DollarSign} color="slate" />
                  <StatCard label={`This Period Transactions`} value={String(comparison.thisPeriod.transactions)} sub={`AOV: Rs. ${comparison.thisPeriod.aov.toLocaleString()}`} trend={comparison.changes.transactionPct} trendUp={comparison.changes.transactionUp} icon={ShoppingBag} color="cyan" />
                  <StatCard label={`Last Period Transactions`} value={String(comparison.lastPeriod.transactions)} sub={`AOV: Rs. ${comparison.lastPeriod.aov.toLocaleString()}`} icon={ShoppingBag} color="slate" />
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-[12px] font-black text-slate-800">Revenue Comparison</h3>
                    <div className="flex items-center gap-3 text-[9px] font-mono text-slate-500">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500 inline-block"/> This Period</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-slate-300 inline-block"/> Last Period</span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <div className="flex items-center justify-between text-[10px] font-bold text-slate-600 mb-1">
                        <span>This Period</span>
                        <span>Rs. {comparison.thisPeriod.revenue.toLocaleString()}</span>
                      </div>
                      <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500 transition-all duration-500" style={{ width: `${Math.min(100, (comparison.thisPeriod.revenue / Math.max(comparison.thisPeriod.revenue, comparison.lastPeriod.revenue)) * 100)}%` }} />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between text-[10px] font-bold text-slate-600 mb-1">
                        <span>Last Period</span>
                        <span>Rs. {comparison.lastPeriod.revenue.toLocaleString()}</span>
                      </div>
                      <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full bg-slate-300 transition-all duration-500" style={{ width: `${Math.min(100, (comparison.lastPeriod.revenue / Math.max(comparison.thisPeriod.revenue, comparison.lastPeriod.revenue)) * 100)}%` }} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button onClick={() => exportCSV('transactions')} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-primary hover:bg-brand-secondary text-white text-[10px] font-black uppercase tracking-wider shadow-sm transition-colors">
                    <Download size={13} /> Export Transactions CSV
                  </button>
                </div>
              </motion.div>
            )}

            {/* SALES TRENDS — Daily Revenue Bar Chart */}
            {activeTab === 'trends' && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                {trends ? (
                  <>
                    {/* Best / Worst Day Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <StatCard label="Best Day" value={trends.bestDay || '—'} sub={trends.dayTotals?.[trends.bestDay] ? `Rs. ${trends.dayTotals[trends.bestDay].toLocaleString()}` : ''} icon={TrendingUp} color="emerald" />
                      <StatCard label="Worst Day" value={trends.worstDay || '—'} sub={trends.dayTotals?.[trends.worstDay] ? `Rs. ${trends.dayTotals[trends.worstDay].toLocaleString()}` : ''} icon={TrendingDown} color="rose" />
                      <StatCard label="Total Transactions" value={String(trends.totalTransactions || 0)} sub="Across all days" icon={ShoppingBag} color="cyan" />
                    </div>

                    {/* Daily Revenue Bar Chart */}
                    <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-[12px] font-black text-slate-800">Daily Revenue Distribution</h3>
                        <span className="text-[9px] text-slate-500 font-mono">All-time by day of week</span>
                      </div>
                      {(() => {
                        const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
                        const fullDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                        const vals = fullDays.map(d => trends.dayTotals?.[d] || 0);
                        const maxVal = Math.max(...vals, 1);
                        return (
                          <div className="flex items-end gap-2 h-40 sm:h-48">
                            {days.map((day, i) => {
                              const pct = (vals[i] / maxVal) * 100;
                              const isBest = fullDays[i] === trends.bestDay;
                              const isWorst = fullDays[i] === trends.worstDay;
                              return (
                                <div key={day} className="flex-1 flex flex-col items-center gap-1">
                                  <span className="text-[8px] font-mono text-slate-500">{vals[i] > 0 ? `Rs.${(vals[i] / 1000).toFixed(0)}k` : ''}</span>
                                  <div className="w-full relative" style={{ height: `${Math.max(pct, 2)}%` }}>
                                    <div className={`absolute inset-0 rounded-t-lg transition-all duration-700 ${isBest ? 'bg-gradient-to-t from-emerald-500 to-emerald-400 shadow-lg shadow-emerald-200' : isWorst ? 'bg-gradient-to-t from-rose-400 to-rose-300' : 'bg-gradient-to-t from-cyan-500 to-cyan-400'}`} />
                                  </div>
                                  <span className={`text-[9px] font-bold ${isBest ? 'text-emerald-600' : isWorst ? 'text-rose-600' : 'text-slate-500'}`}>{day}</span>
                                  {isBest && <span className="text-[7px] font-black text-emerald-600 uppercase">Best</span>}
                                  {isWorst && <span className="text-[7px] font-black text-rose-600 uppercase">Worst</span>}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}

                      {/* Day counts row */}
                      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, i) => {
                          const fullDay = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][i];
                          const count = trends.dayCounts?.[fullDay] || 0;
                          return (
                            <div key={day} className="flex-1 text-center">
                              <span className="text-[8px] text-slate-400">{count} txn{count !== 1 ? 's' : ''}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                    <BarChart3 size={28} className="mb-2 opacity-40" />
                    <p className="text-[12px] font-semibold">No trend data available</p>
                    <p className="text-[10px] mt-1">Sales trends require manager access and completed transactions</p>
                    <button onClick={fetchAll} className="mt-3 px-4 py-2 rounded-lg bg-brand-primary text-white text-[10px] font-bold">Retry</button>
                  </div>
                )}
              </motion.div>
            )}

            {/* CUSTOMERS */}
            {activeTab === 'customers' && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                {customers ? (
                  <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <StatCard label="Total Customers" value={String(customers.summary.totalCustomers)} sub="All-time registered" icon={Users} color="cyan" />
                  <StatCard label="Repeat Customers" value={String(customers.summary.repeatCustomers)} sub={`${customers.summary.repeatRate} repeat rate`} icon={TrendingUp} color="emerald" />
                  <StatCard label="Avg Customer Value" value={`Rs. ${customers.summary.avgCLV.toLocaleString()}`} sub="Lifetime value per customer" icon={DollarSign} color="amber" />
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                    <h3 className="text-[12px] font-black text-slate-800">Top Customers</h3>
                    <button onClick={() => exportCSV('customers')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-[9px] font-bold transition-colors">
                      <Download size={11} /> CSV
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-[11px]">
                      <thead>
                        <tr className="border-b border-slate-200 text-slate-500 font-extrabold uppercase tracking-wider bg-slate-50/60">
                          <th className="py-2.5 px-3">Customer</th>
                          <th className="py-2.5 px-3 hidden sm:table-cell">Phone</th>
                          <th className="py-2.5 px-3 text-right">Total Spent</th>
                          <th className="py-2.5 px-3 text-right hidden md:table-cell">Visits</th>
                          <th className="py-2.5 px-3 text-right">CLV</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {customers.topCustomers.map((c: any, i: number) => (
                          <tr key={c.id} className="hover:bg-slate-50 transition-colors">
                            <td className="py-2.5 px-3">
                              <div className="flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-brand-primary/10 text-brand-primary flex items-center justify-center text-[9px] font-black">{i + 1}</span>
                                <span className="font-bold text-slate-800">{c.name}</span>
                              </div>
                            </td>
                            <td className="py-2.5 px-3 font-mono text-slate-600 hidden sm:table-cell">{c.phone}</td>
                            <td className="py-2.5 px-3 text-right font-mono font-bold text-slate-800">Rs. {c.totalSpent?.toLocaleString()}</td>
                            <td className="py-2.5 px-3 text-right font-mono text-slate-600 hidden md:table-cell">{c.totalVisits}</td>
                            <td className="py-2.5 px-3 text-right font-mono font-bold text-emerald-600">Rs. {c.clv?.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                    <Users size={28} className="mb-2 opacity-40" />
                    <p className="text-[12px] font-semibold">No customer data available</p>
                    <p className="text-[10px] mt-1">Customer analytics appear after transactions with customer info</p>
                    <button onClick={fetchAll} className="mt-3 px-4 py-2 rounded-lg bg-brand-primary text-white text-[10px] font-bold">Retry</button>
                  </div>
                )}
              </motion.div>
            )}

            {/* PRODUCTS */}
            {activeTab === 'products' && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                {products ? (
                  <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <StatCard label="Total Products" value={String(products.summary.totalProducts)} sub="In catalog" icon={Package} color="violet" />
                  <StatCard label="Out of Stock" value={String(products.summary.zeroStock)} sub="Need restocking" icon={TrendingDown} color="rose" />
                  <StatCard label="All-Time Revenue" value={`Rs. ${products.summary.allTimeRevenue.toLocaleString()}`} sub="From all products" icon={DollarSign} color="emerald" />
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                    <h3 className="text-[12px] font-black text-slate-800">Product Performance</h3>
                    <div className="flex items-center gap-2">
                      <select value={productSort} onChange={e => setProductSort(e.target.value as 'revenue' | 'qty')} className="text-[9px] font-bold text-slate-600 bg-slate-100 border-0 rounded-lg px-2 py-1.5 outline-none">
                        <option value="revenue">By Revenue</option>
                        <option value="qty">By Quantity</option>
                      </select>
                      <button onClick={() => exportCSV('products')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-[9px] font-bold transition-colors">
                        <Download size={11} /> CSV
                      </button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-[11px]">
                      <thead>
                        <tr className="border-b border-slate-200 text-slate-500 font-extrabold uppercase tracking-wider bg-slate-50/60">
                          <th className="py-2.5 px-3">Product</th>
                          <th className="py-2.5 px-3 hidden sm:table-cell">Category</th>
                          <th className="py-2.5 px-3 text-right hidden md:table-cell">Qty Sold</th>
                          <th className="py-2.5 px-3 text-right">Revenue</th>
                          <th className="py-2.5 px-3 text-right">Margin</th>
                          <th className="py-2.5 px-3 text-right hidden sm:table-cell">Stock</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {products.products.map((p: any, i: number) => (
                          <tr key={p.productId} className="hover:bg-slate-50 transition-colors">
                            <td className="py-2.5 px-3">
                              <div className="flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-violet-100 text-violet-600 flex items-center justify-center text-[9px] font-black">{i + 1}</span>
                                <span className="font-bold text-slate-800">{p.name}</span>
                              </div>
                            </td>
                            <td className="py-2.5 px-3 text-slate-600 hidden sm:table-cell">{p.category}</td>
                            <td className="py-2.5 px-3 text-right font-mono text-slate-700 hidden md:table-cell">{p.totalQty}</td>
                            <td className="py-2.5 px-3 text-right font-mono font-bold text-slate-800">Rs. {p.totalRevenue.toLocaleString()}</td>
                            <td className={`py-2.5 px-3 text-right font-mono font-bold ${parseFloat(p.marginPct) >= 30 ? 'text-emerald-600' : parseFloat(p.marginPct) >= 15 ? 'text-amber-600' : 'text-rose-600'}`}>{p.marginPct}%</td>
                            <td className={`py-2.5 px-3 text-right font-mono hidden sm:table-cell ${p.stockCount <= 0 ? 'text-rose-600 font-bold' : 'text-slate-600'}`}>{p.stockCount <= 0 ? 'OUT' : p.stockCount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                    <Package size={28} className="mb-2 opacity-40" />
                    <p className="text-[12px] font-semibold">No product data available</p>
                    <p className="text-[10px] mt-1">Product analytics appear after sales with product info</p>
                    <button onClick={fetchAll} className="mt-3 px-4 py-2 rounded-lg bg-brand-primary text-white text-[10px] font-bold">Retry</button>
                  </div>
                )}
              </motion.div>
            )}

            {/* CASHIERS */}
            {activeTab === 'cashiers' && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                {cashiers ? (
                  <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <StatCard label="Period" value={cashiers.period} sub={`${cashiers.startDate} → ${cashiers.endDate}`} icon={Clock} color="cyan" />
                  <StatCard label="Active Cashiers" value={String(cashiers.cashiers.length)} sub="With transactions" icon={Users} color="violet" />
                  <StatCard label="Total Transactions" value={String(cashiers.cashiers.reduce((s: number, c: any) => s + c.txCount, 0))} sub="In period" icon={ShoppingBag} color="emerald" />
                  <StatCard label="Total Revenue" value={`Rs. ${cashiers.cashiers.reduce((s: number, c: any) => s + c.totalRevenue, 0).toLocaleString()}`} sub="By all staff" icon={DollarSign} color="amber" />
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                  <div className="px-4 py-3 border-b border-slate-100">
                    <h3 className="text-[12px] font-black text-slate-800">Cashier Performance</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-[11px]">
                      <thead>
                        <tr className="border-b border-slate-200 text-slate-500 font-extrabold uppercase tracking-wider bg-slate-50/60">
                          <th className="py-2.5 px-3">Cashier</th>
                          <th className="py-2.5 px-3 hidden sm:table-cell">Role</th>
                          <th className="py-2.5 px-3 text-right">Txns</th>
                          <th className="py-2.5 px-3 text-right">Revenue</th>
                          <th className="py-2.5 px-3 text-right hidden md:table-cell">Avg Ticket</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {cashiers.cashiers.map((c: any, i: number) => (
                          <tr key={c.cashierId} className="hover:bg-slate-50 transition-colors">
                            <td className="py-2.5 px-3">
                              <div className="flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-[9px] font-black">{i + 1}</span>
                                <span className="font-bold text-slate-800">{c.name}</span>
                              </div>
                            </td>
                            <td className="py-2.5 px-3 hidden sm:table-cell">
                              <span className="px-2 py-0.5 rounded-md border text-[9px] font-bold uppercase bg-slate-100 text-slate-600 border-slate-200">{c.role}</span>
                            </td>
                            <td className="py-2.5 px-3 text-right font-mono text-slate-700">{c.txCount}</td>
                            <td className="py-2.5 px-3 text-right font-mono font-bold text-slate-800">Rs. {c.totalRevenue.toLocaleString()}</td>
                            <td className="py-2.5 px-3 text-right font-mono text-slate-600 hidden md:table-cell">Rs. {c.avgTicket.toLocaleString()}</td>
                          </tr>
                        ))}
                        {cashiers.cashiers.length === 0 && (
                          <tr><td colSpan={5} className="py-8 text-center text-slate-400">No cashier data for this period</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                    <Clock size={28} className="mb-2 opacity-40" />
                    <p className="text-[12px] font-semibold">No staff data available</p>
                    <p className="text-[10px] mt-1">Staff performance appears after cashier transactions</p>
                    <button onClick={fetchAll} className="mt-3 px-4 py-2 rounded-lg bg-brand-primary text-white text-[10px] font-bold">Retry</button>
                  </div>
                )}
              </motion.div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvancedAnalytics;
